import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { api } from "../lib/api";
import ExportButtons from "./ExportButtons";
import { downloadCSV, downloadPDF } from "../lib/exports";
import { useSort, usePersistedState } from "../lib/useSort";
import { SortHeader } from "./SortControls";
import PeriodBadge from "./PeriodBadge";

const COLUMN_LABEL = {
  "Public Butter Kensington": "PB",
  "Black Market 347": "347",
  "Black Market 256": "256",
  "Dirty Mike's": "DM",
};

const SIZE_ORDER = ["S", "M", "L", "XL", "XXL", "LS", "TT", "OTH"];
function tidySizeKey(raw) {
  const k = (raw || "").toUpperCase().trim();
  if (k.startsWith("LONG SLEEVE")) return "LS";
  if (k.startsWith("TANK TOP")) return "TT";
  if (["S", "M", "L", "XL", "XXL"].includes(k)) return k;
  return "OTH";
}
function formatSizes(bySize) {
  const buckets = {};
  for (const [k, v] of Object.entries(bySize || {})) {
    if (!v) continue;
    const b = tidySizeKey(k);
    buckets[b] = (buckets[b] || 0) + v;
  }
  const entries = Object.entries(buckets);
  if (!entries.length) return null;
  entries.sort((a, b) => SIZE_ORDER.indexOf(a[0]) - SIZE_ORDER.indexOf(b[0]));
  return entries.map(([s, v]) => `${s} ${v}`).join(" · ");
}

function stripPrefix(name) {
  return name.replace(/^\s*#Printed\s*Tee\s*[-–:]\s*/i, "").trim();
}

function buildTeeExport(rows, outlets, rangeLabel) {
  const columns = [
    { key: "n", label: "#", align: "right" },
    { key: "design", label: "Design" },
    { key: "category", label: "Category" },
    { key: "legs", label: "Legs" },
    { key: "retention", label: "Retention %", align: "right" },
    { key: "first_sale", label: "First sale" },
    { key: "growth", label: "Δ vs prior (%)", align: "right" },
    ...outlets.map((o) => ({
      key: `outlet_${o.outlet_id}`,
      label: COLUMN_LABEL[o.name] || o.name,
      align: "right",
    })),
    { key: "total_units", label: "Total units", align: "right" },
    { key: "revenue", label: "Revenue", align: "right" },
  ];
  const flat = rows.map((r, i) => {
    const row = {
      n: i + 1,
      design: stripPrefix(r.name),
      category: r.category || "",
      legs: r.legs_label || "",
      retention: r.retention_ratio != null ? Math.round(r.retention_ratio * 100) : "",
      first_sale: r.first_sale_date || "",
      growth: r.unit_growth_pct != null ? r.unit_growth_pct.toFixed(1) : "",
      total_units: r.total_units || 0,
      revenue: r.total_revenue != null ? `$${Math.round(r.total_revenue).toLocaleString()}` : "",
    };
    for (const o of outlets) row[`outlet_${o.outlet_id}`] = r.units_by_outlet?.[o.outlet_id] || 0;
    return row;
  });
  return {
    filenameBase: `printed-tees-${rangeLabel || "range"}`,
    meta: {
      title: "Printed Tees",
      subtitle: `Top house-manufactured designs · ${rangeLabel || ""}`,
    },
    columns,
    rows: flat,
  };
}

function UnitDelta({ pct }) {
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-0.5 font-mono-bm text-[11px] text-neutral-400" title="No prior-period baseline">
        <Minus size={11} strokeWidth={2} />
        new
      </span>
    );
  }
  const up = pct >= 0;
  const cls = up ? "text-emerald-700" : "text-red-700";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono-bm text-[11px] font-semibold ${cls}`}>
      <Icon size={11} strokeWidth={2.5} />
      {up ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

function Sparkline({ series, color = "#0a0a0a" }) {
  if (!series?.length) return <div className="w-[100px] h-6" />;
  const w = 100, h = 24, pad = 2;
  const values = series.map((s) => s.units);
  const max = Math.max(1, ...values);
  const stepX = values.length > 1 ? (w - pad * 2) / (values.length - 1) : 0;
  const pts = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - (v / max) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = values[values.length - 1];
  const lastX = pad + (values.length - 1) * stepX;
  const lastY = h - pad - (last / max) * (h - pad * 2);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="1.6" fill={color} />
    </svg>
  );
}

const LEGS_BADGE = {
  "has legs": "text-emerald-700 border-emerald-700/40 bg-emerald-50/60",
  "steady fade": "text-neutral-700 border-neutral-500/40 bg-neutral-100/40",
  "one-hit": "text-[#c72c2c] border-[#c72c2c]/40 bg-[#c72c2c]/5",
};

export default function PrintedTees({ range, outletIds = [] }) {
  const [data, setData] = useState({ outlets: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | legs | one_hit | recent
  const [visibleCount, setVisibleCount] = useState(30);
  const [mode, setMode] = usePersistedState("bm-mode:printed-tees", "units"); // units | pct (store cells as % of design total)

  const scopeKey = outletIds.join(",");
  useEffect(() => {
    if (!range) return;
    let cancel = false;
    setLoading(true);
    const params = { start_date: range.start, end_date: range.end, limit: 2000 };
    if (scopeKey) params.outlet_ids = scopeKey;
    api
      .get("/products/printed-tees", { params })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range?.start, range?.end, scopeKey]);

  useEffect(() => { setVisibleCount(30); }, [filter, range?.start, range?.end, scopeKey]);

  const outlets = data.outlets || [];
  const allRows = data.rows || [];

  const rows = useMemo(() => {
    switch (filter) {
      case "legs":
        return allRows.filter((r) => r.legs_label === "has legs");
      case "one_hit":
        return allRows.filter((r) => r.legs_label === "one-hit");
      case "recent": {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 90);
        return allRows.filter((r) => r.first_sale_date && new Date(r.first_sale_date) >= cutoff);
      }
      default:
        return allRows;
    }
  }, [allRows, filter]);

  const totals = useMemo(() => {
    return outlets.reduce((acc, o) => {
      acc[o.outlet_id] = rows.reduce((s, r) => s + (r.units_by_outlet?.[o.outlet_id] || 0), 0);
      return acc;
    }, {});
  }, [outlets, rows]);
  const grandTotal = rows.reduce((s, r) => s + (r.total_units || 0), 0);

  // Overall rank = position in the full backend-ordered list (by total units),
  // stable regardless of the current filter / sort.
  const rankById = useMemo(() => {
    const m = new Map();
    allRows.forEach((r, i) => m.set(r.product_id, i + 1));
    return m;
  }, [allRows]);

  const sort = useSort("rank", "asc", {
    rank: (r) => rankById.get(r.product_id) ?? 1e9,
    design: (r) => stripPrefix(r.name).toLowerCase(),
    growth: (r) => r.unit_growth_pct,
    units: (r) => r.total_units,
    ...Object.fromEntries(
      outlets.map((o) => [`store_${o.outlet_id}`, (r) => r.units_by_outlet?.[o.outlet_id] || 0]),
    ),
  }, ["design", "rank"], "printed-tees");
  const sorted = useMemo(() => sort.apply(rows), [rows, sort]);

  const counts = useMemo(() => {
    const c = { all: allRows.length, legs: 0, one_hit: 0, recent: 0 };
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);
    for (const r of allRows) {
      if (r.legs_label === "has legs") c.legs += 1;
      if (r.legs_label === "one-hit") c.one_hit += 1;
      if (r.first_sale_date && new Date(r.first_sale_date) >= cutoff) c.recent += 1;
    }
    return c;
  }, [allRows]);

  const chips = [
    { key: "all", label: "All", n: counts.all },
    { key: "legs", label: "Has legs", n: counts.legs },
    { key: "recent", label: "Recent · 90d", n: counts.recent },
    { key: "one_hit", label: "One-hits", n: counts.one_hit },
  ];

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="printed-tees">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <div className="bm-eyebrow">Table 9 &middot; House-made</div>
        <div className="bm-eyebrow text-neutral-500">{range?.label || ""}</div>
      </div>
      <h3 className="bm-headline text-3xl mb-1">Printed Tees.</h3>
      <PeriodBadge range={range} className="mb-2" />
      <p className="bm-eyebrow text-neutral-500 mb-4">
        Every house-manufactured design &middot; unit sales by store &middot; size split &middot; trend + Δ vs prior period
      </p>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="printed-tees-filters">
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => setFilter(c.key)}
            data-testid={`printed-tees-filter-${c.key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 bm-eyebrow border border-[#0a0a0a] ${
              filter === c.key ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"
            }`}
          >
            {c.label}
            <span className={`font-mono-bm ${filter === c.key ? "opacity-70" : "opacity-50"}`}>{c.n}</span>
          </button>
        ))}
        <div className="flex-1" />
        <div className="flex border border-[#0a0a0a]" role="group" aria-label="Store value mode" data-testid="tees-mode-toggle">
          {[
            { k: "units", label: "Units" },
            { k: "pct", label: "% of total" },
          ].map((m, i) => (
            <button
              key={m.k}
              type="button"
              onClick={() => setMode(m.k)}
              data-testid={`tees-mode-${m.k}`}
              title={m.k === "pct" ? "Show each store as a % of the design's total sales" : "Show raw units"}
              className={`px-2.5 py-1.5 bm-eyebrow ${i > 0 ? "border-l border-[#0a0a0a]" : ""} ${
                mode === m.k ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <ExportButtons
          disabled={!rows.length}
          testIdPrefix="printed-tees-export"
          onCSV={() => {
            const e = buildTeeExport(sorted, outlets, range?.label);
            downloadCSV(e.filenameBase, e.columns, e.rows);
          }}
          onPDF={() => {
            const e = buildTeeExport(sorted, outlets, range?.label);
            downloadPDF(e.filenameBase, e.meta, e.columns, e.rows);
          }}
        />
      </div>

      {loading && rows.length === 0 && <div className="text-sm text-neutral-500">Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-sm text-neutral-600">Nothing matches this filter.</div>}

      {rows.length > 0 && (
        <>
        {/* Phone: card rows */}
        <div className="md:hidden" data-testid="printed-tees-cards">
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="bm-eyebrow text-neutral-500">Sort</span>
            <select
              data-testid="tees-mobile-sort"
              value={`${sort.sortKey}:${sort.sortDir}`}
              onChange={(e) => { const [k, d] = e.target.value.split(":"); sort.set(k, d); }}
              className="flex-1 max-w-[240px] bg-transparent border border-[#0a0a0a] px-3 py-2 font-mono-bm text-xs"
            >
              <option value="rank:asc">Rank</option>
              <option value="units:desc">Most units</option>
              <option value="growth:desc">Biggest gain vs prior</option>
              <option value="growth:asc">Biggest drop vs prior</option>
              <option value="design:asc">Design A–Z</option>
              {outlets.map((o) => (
                <option key={o.outlet_id} value={`store_${o.outlet_id}:desc`}>Most at {COLUMN_LABEL[o.name] || o.name}</option>
              ))}
            </select>
          </div>
          <div className="divide-y divide-[#0a0a0a]/15 border-t border-[#0a0a0a]">
            {sorted.slice(0, visibleCount).map((r, i) => {
              const legs = LEGS_BADGE[r.legs_label] || "text-neutral-400 border-neutral-300";
              return (
                <div key={r.product_id || i} className="py-3" data-testid={`printed-tee-card-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-baseline gap-2">
                      <span className="font-mono-bm text-xs text-neutral-500 shrink-0">{String(rankById.get(r.product_id) || i + 1).padStart(2, "0")}</span>
                      <div className="min-w-0">
                        <div className="font-medium leading-snug">{stripPrefix(r.name)}</div>
                        <div className="bm-eyebrow text-neutral-500 font-mono-bm mt-0.5">{formatSizes(r.units_by_size) || "—"}</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-bm font-semibold tabular-nums text-base">{r.total_units.toLocaleString()}</div>
                      <div className="bm-eyebrow text-neutral-500">units</div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <UnitDelta pct={r.unit_growth_pct} />
                    {r.legs_label && (
                      <span className={`inline-flex items-center px-1.5 py-0.5 border ${legs} text-[9px] font-mono-bm uppercase tracking-wider`}>
                        {r.legs_label}{r.retention_ratio != null && <span className="ml-1 opacity-70">{Math.round(r.retention_ratio * 100)}%</span>}
                      </span>
                    )}
                    <span className="font-mono-bm text-xs text-neutral-600 tabular-nums">
                      {outlets.map((o) => {
                        const u = r.units_by_outlet?.[o.outlet_id] || 0;
                        const pct = r.total_units ? Math.round((u / r.total_units) * 100) : 0;
                        return `${COLUMN_LABEL[o.name] || o.name} ${u === 0 ? "—" : mode === "pct" ? `${pct}%` : u}`;
                      }).join(" · ")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t-2 border-[#0a0a0a] py-2 flex items-center justify-between bm-eyebrow text-neutral-700">
            <span>Column totals</span>
            <span className="font-mono-bm tabular-nums">
              {outlets.map((o) => { const t = totals[o.outlet_id] || 0; const pct = grandTotal ? Math.round((t / grandTotal) * 100) : 0; return `${COLUMN_LABEL[o.name] || o.name} ${mode === "pct" ? (t === 0 ? "—" : `${pct}%`) : t.toLocaleString()}`; }).join(" · ")}
              {" · "}<span className="font-bold text-[#0a0a0a]">{grandTotal.toLocaleString()}</span>
            </span>
          </div>
        </div>

        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm" data-testid="printed-tees-table">
            <thead>
              <tr className="border-b border-[#0a0a0a]">
                <th className="text-left bm-eyebrow text-neutral-600 py-2 pr-3 w-10">
                  <SortHeader label="#" k="rank" state={sort} align="left" testId="tees-sort-rank" />
                </th>
                <th className="text-left bm-eyebrow text-neutral-600 py-2 pr-3">
                  <SortHeader label="Design" k="design" state={sort} align="left" testId="tees-sort-design" />
                </th>
                <th className="text-left bm-eyebrow text-neutral-600 py-2 px-3 w-28">Legs</th>
                <th className="text-left bm-eyebrow text-neutral-600 py-2 px-3 w-[110px]">Trend</th>
                <th className="text-right bm-eyebrow text-neutral-600 py-2 px-3 w-20">
                  <SortHeader label="Δ vs prior" k="growth" state={sort} testId="tees-sort-growth" />
                </th>
                {outlets.map((o) => (
                  <th key={o.outlet_id} className="text-right bm-eyebrow text-neutral-600 py-2 px-3 w-16">
                    <SortHeader
                      label={COLUMN_LABEL[o.name] || o.name}
                      k={`store_${o.outlet_id}`}
                      state={sort}
                      testId={`tees-sort-store-${o.outlet_id}`}
                    />
                  </th>
                ))}
                <th className="text-right bm-eyebrow text-neutral-600 py-2 pl-3 w-20">
                  <SortHeader label="Units" k="units" state={sort} testId="tees-sort-units" />
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, visibleCount).map((r, i) => {
                const legs = LEGS_BADGE[r.legs_label] || "text-neutral-400 border-neutral-300";
                return (
                  <tr key={r.product_id || i} className="border-b border-[#0a0a0a]/15 hover:bg-[#0a0a0a]/5" data-testid={`printed-tee-row-${i}`}>
                    <td className="py-2 pr-3 font-mono-bm text-xs text-neutral-500 align-middle">
                      {String(rankById.get(r.product_id) || i + 1).padStart(2, "0")}
                    </td>
                    <td className="py-2 pr-3 align-middle">
                      <div className="font-medium truncate max-w-[280px]" title={r.name}>{stripPrefix(r.name)}</div>
                      <div className="bm-eyebrow text-neutral-500 font-mono-bm" data-testid={`printed-tee-sizes-${i}`}>
                        {formatSizes(r.units_by_size) || "—"}
                      </div>
                    </td>
                    <td className="py-2 px-3 align-middle">
                      {r.legs_label ? (
                        <span className={`inline-flex items-center px-1.5 py-0.5 border ${legs} text-[9px] font-mono-bm uppercase tracking-wider`}>
                          {r.legs_label}
                          {r.retention_ratio != null && (
                            <span className="ml-1 opacity-70">{Math.round(r.retention_ratio * 100)}%</span>
                          )}
                        </span>
                      ) : (
                        <span className="bm-eyebrow text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 align-middle">
                      <Sparkline series={r.series} />
                    </td>
                    <td className="py-2 px-3 text-right align-middle">
                      <UnitDelta pct={r.unit_growth_pct} />
                    </td>
                    {outlets.map((o) => {
                      const u = r.units_by_outlet?.[o.outlet_id] || 0;
                      const pct = r.total_units ? Math.round((u / r.total_units) * 100) : 0;
                      return (
                        <td key={o.outlet_id} className={`py-2 px-3 text-right font-mono-bm tabular-nums align-middle ${u === 0 ? "text-neutral-300" : ""}`}>
                          {u === 0 ? "—" : mode === "pct" ? `${pct}%` : u.toLocaleString()}
                        </td>
                      );
                    })}
                    <td className="py-2 pl-3 text-right font-mono-bm font-semibold tabular-nums align-middle">
                      {r.total_units.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#0a0a0a]">
                <td></td>
                <td className="py-2 pr-3 bm-eyebrow text-neutral-700">Column totals</td>
                <td colSpan={3}></td>
                {outlets.map((o) => {
                  const t = totals[o.outlet_id] || 0;
                  const pct = grandTotal ? Math.round((t / grandTotal) * 100) : 0;
                  return (
                    <td key={o.outlet_id} className="py-2 px-3 text-right font-mono-bm font-semibold tabular-nums">
                      {mode === "pct" ? (t === 0 ? "—" : `${pct}%`) : t.toLocaleString()}
                    </td>
                  );
                })}
                <td className="py-2 pl-3 text-right font-mono-bm font-bold tabular-nums">
                  {grandTotal.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
          {rows.length > visibleCount && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                onClick={() => setVisibleCount((n) => n + 30)}
                data-testid="printed-tees-show-more"
                className="bm-eyebrow px-4 py-2 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-[#f5f2eb]"
              >
                Show next {Math.min(30, rows.length - visibleCount)} · {visibleCount} of {rows.length}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
